import React, { useState, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Icon from 'components/AppIcon';
import Button from 'components/ui/Button';
import { useNavigate } from 'react-router-dom';
import axiosInstance, { API_BASE_URL } from 'config/axiosInstance';
import { Link } from "react-router-dom";

const RecordedWebinarsPage = () => {
  const navigate = useNavigate();
  const [isMobile, setIsMobile] = useState(false);
  
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const [searchQuery, setSearchQuery] = useState('');
  const [selectedMonths, setSelectedMonths] = useState([]);
  const [selectedInstructors, setSelectedInstructors] = useState([]);
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [sortBy, setSortBy] = useState('most-reviewed');
  const [currentPage, setCurrentPage] = useState(1);
  const [showFilters, setShowFilters] = useState(false);
  const [showMobileDrawer, setShowMobileDrawer] = useState(false);
  const itemsPerPage = 12;

  // ✅ NEW: Progressive loading states
  const [webinars, setWebinars] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [progress, setProgress] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  
  const durationOptions = [
    { value: '60', label: '1 hour' },
    { value: '75', label: '1.25 hours' },
    { value: '90', label: '1.5 hours' },
    { value: '120', label: '2 hours' },
    { value: '180', label: '3 hours' },
    { value: '240', label: '4 hours' },
    { value: '360', label: '6 hours' },
    { value: 'custom', label: 'Custom duration' }
  ];
const calculateDurationFromRecording = (recordingDurationMinutes) => {
  if (
    recordingDurationMinutes === null ||
    recordingDurationMinutes === undefined ||
    isNaN(recordingDurationMinutes) ||
    recordingDurationMinutes <= 0
  ) {
    return null;
  }

  const predefinedDurations = durationOptions
    .filter(opt => opt.value !== 'custom')
    .map(opt => parseInt(opt.value, 10))
    .sort((a, b) => a - b);

  // Find the smallest duration that can contain the recording
  for (const duration of predefinedDurations) {
    if (recordingDurationMinutes <= duration) {
      return duration.toString();
    }
  }

  // If recording exceeds all predefined values
  return 'custom';
};

  useEffect(() => {
    if (showMobileDrawer) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [showMobileDrawer]);

  // ✅ FIXED: Progressive loading with DYNAMIC total count
const fetchWebinars = async () => {
  try {
    setLoading(true);
    setError(null);
    setWebinars([]);
    setProgress(0);
    setTotalCount(0);

    let nextUrl = `/webinars/?webinar_type=recorded&status=available&page_size=20`;
    let totalCountLocal = 0; // ✅ LOCAL variable - no stale closure!

    while (nextUrl) {
      console.log(`🔄 Fetching recorded: ${nextUrl}`);
      
      const response = await axiosInstance.get(nextUrl);
      
      // ✅ FIRST RESPONSE: Get total count
      if (totalCountLocal === 0) {
        totalCountLocal = response.data.count || response.data.results?.length || 0;
        setTotalCount(totalCountLocal); // Update state for UI
        console.log(`📊 TOTAL recorded webinars: ${totalCountLocal}`);
      }
      
      const webinarsPage = response.data.results 
        ? response.data.results.map(transformWebinarData)
        : response.data.map(transformWebinarData);
      
      // ✅ PASS totalCountLocal to callback - NO STALE CLOSURE!
      setWebinars(prevWebinars => {
        const updatedWebinars = [...prevWebinars, ...webinarsPage];
        const sortedWebinars = updatedWebinars.sort((a, b) => 
          new Date(b.date) - new Date(a.date)
        );
        
        // ✅ Use LOCAL variable - always correct!
        const newProgress = totalCountLocal > 0 
          ? Math.min(100, (sortedWebinars.length / totalCountLocal) * 100)
          : 0;
        
        setProgress(newProgress);
        
        console.log(`✅ Added ${webinarsPage.length} | Total: ${sortedWebinars.length}/${totalCountLocal} (${Math.round(newProgress)}%)`);
        return sortedWebinars;
      });
      
      nextUrl = response.data.next;
      
      if (nextUrl) {
        await new Promise(resolve => setTimeout(resolve, 500));
      }
    }
    
  } catch (err) {
    console.error('❌ Error fetching recorded webinars:', err);
    setError('Failed to load recorded webinars. Please try again later.');
  } finally {
    setLoading(false);
    console.log(`🎉 Recorded loading complete! Total: ${webinars.length}`);
  }
};


  useEffect(() => {
    fetchWebinars();
  }, [API_BASE_URL]);

  const transformWebinarData = (apiWebinar) => {
    const createdDate = new Date(apiWebinar.created_at);
      const rawDurationMinutes =
    apiWebinar.duration ||
    apiWebinar.recording_duration ||
    apiWebinar.duration_minutes ||
    null;

  const calculatedDuration = calculateDurationFromRecording(rawDurationMinutes);
    return {
      id: apiWebinar.id,
      webinarId: apiWebinar.webinar_id,
      title: apiWebinar.title,
      description: apiWebinar.description,
      
      platform_prices: apiWebinar.platform_prices,
      applicable_prices: apiWebinar.applicable_prices,
      current_platform: apiWebinar.current_platform,
      main_price: parseFloat(apiWebinar.main_price || 0),
      is_free: apiWebinar.is_free || false,
         duration: calculatedDuration || '60',
         rawDurationMinutes,
      instructor: {
        name: apiWebinar.speaker?.full_name || 'Unknown Instructor',
        avatar: apiWebinar.speaker?.avatar || 
          `https://ui-avatars.com/api/?name=${encodeURIComponent(apiWebinar.speaker?.full_name || 'Unknown')}&background=14b8a6&color=fff`,
      },
      
      date: apiWebinar.created_at,
      month: createdDate.toLocaleString('default', { month: 'short' }).toUpperCase(),
      year: createdDate.getFullYear(),
      
      
      category: apiWebinar.category?.name || 'General',
      enrollments: apiWebinar.enrolled_count || 0,
      rating: apiWebinar.analytics?.average_rating || 4.8,
      
      price: apiWebinar.applicable_prices?.recorded_single 
        ? parseFloat(apiWebinar.applicable_prices.recorded_single)
        : parseFloat(apiWebinar.main_price || 0),
      
      originalPrice: apiWebinar.applicable_prices?.recorded_single 
        ? Math.round(parseFloat(apiWebinar.applicable_prices.recorded_single) * 1.3 * 100) / 100
        : Math.round(parseFloat(apiWebinar.main_price || 0) * 1.3 * 100) / 100,
      
      image: apiWebinar.cover_image_url || 'https://images.unsplash.com/photo-1516321318423-f06f85e504b3?ixlib=rb-4.0.3&auto=format&fit=crop&w=600&q=80',
      level: apiWebinar.skill_level || 'All Levels',
      
      user_context: apiWebinar.user_context,
      hasFullAccess: apiWebinar.can_access_webinar || false,
      recording_links: apiWebinar.recording_links,
    };
  };

  const filterOptions = useMemo(() => {
    const months = [...new Set(webinars.map(w => new Date(w.date).toLocaleString('default', { month: 'long' })))];
    const instructors = [...new Set(webinars.map(w => w.instructor.name))];
    const categories = [...new Set(webinars.map(w => w.category))];
    return { months, instructors, categories };
  }, [webinars]);

  const handleMonthToggle = (month) => {
    setSelectedMonths(prev => 
      prev.includes(month) ? prev.filter(m => m !== month) : [...prev, month]
    );
  };

  const handleInstructorToggle = (instructor) => {
    setSelectedInstructors(prev => 
      prev.includes(instructor) ? prev.filter(i => i !== instructor) : [...prev, instructor]
    );
  };

  const handleCategoryToggle = (category) => {
    setSelectedCategories(prev => 
      prev.includes(category) ? prev.filter(c => c !== category) : [...prev, category]
    );
  };

  const filteredWebinars = useMemo(() => {
  let filtered = webinars.filter(webinar => {
    const matchesSearch = searchQuery === '' || 
      webinar.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      webinar.instructor.name.toLowerCase().includes(searchQuery.toLowerCase());

    const webinarMonth = new Date(webinar.date).toLocaleString('default', { month: 'long' });
    const matchesMonth = selectedMonths.length === 0 || selectedMonths.includes(webinarMonth);
    
    const matchesInstructor = selectedInstructors.length === 0 || selectedInstructors.includes(webinar.instructor.name);
    const matchesCategory = selectedCategories.length === 0 || selectedCategories.includes(webinar.category);

    return matchesSearch && matchesMonth && matchesInstructor && matchesCategory;
  });

  filtered.sort((a, b) => {
    switch (sortBy) {
      case 'most-reviewed':
        return (b.enrollments || 0) - (a.enrollments || 0);
      case 'most-viewed':
        return (b.views || 0) - (a.views || 0);
      case 'newest':
        return new Date(b.date) - new Date(a.date);
      case 'price-low':
        return a.price - b.price;
      case 'price-high':
        return b.price - a.price;
      default:
        return 0;
    }
  });

  return filtered;
}, [webinars, searchQuery, selectedMonths, selectedInstructors, selectedCategories, sortBy]);

  const totalPages = Math.ceil(filteredWebinars.length / itemsPerPage);
  const paginatedWebinars = filteredWebinars.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, selectedMonths, selectedInstructors, selectedCategories]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [currentPage]);

  const generateSlug = (title) => {
    return title
      .toLowerCase()
      .replace(/[^a-z0-9\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .trim();
  };
  
  const clearFilters = () => {
    setSearchQuery('');
    setSelectedMonths([]);
    setSelectedInstructors([]);
    setSelectedCategories([]);
    setCurrentPage(1);
  };

  const hasActiveFilters = searchQuery || selectedMonths.length > 0 || selectedInstructors.length > 0 || selectedCategories.length > 0;
  const activeFilterCount = [searchQuery, ...selectedMonths, ...selectedInstructors, ...selectedCategories].filter(Boolean).length;

  const handleApplyFilters = () => {
    setShowMobileDrawer(false);
  };

  const FilterSection = () => (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
      <div>
        <h4 className="font-bold text-gray-900 mb-3 flex items-center">
          <motion.div
            animate={{ rotate: [0, 5, 0] }}
            transition={{ duration: 2, repeat: Infinity }}
          >
            <Icon name="Calendar" size={16} className="mr-2 text-green-600" />
          </motion.div>
          Month ({selectedMonths.length})
        </h4>
        <div className="space-y-2 max-h-48 overflow-y-auto pr-2">
          {filterOptions.months.map((month) => (
            <motion.label
              key={month}
              whileHover={{ x: 5 }}
              className="flex items-center p-2 rounded-lg hover:bg-white cursor-pointer transition-all duration-300"
            >
              <input
                type="checkbox"
                checked={selectedMonths.includes(month)}
                onChange={() => handleMonthToggle(month)}
                className="w-4 h-4 text-green-600 border-gray-300 rounded focus:ring-green-500 focus:ring-2"
              />
              <span className="ml-3 text-sm text-gray-700">{month}</span>
            </motion.label>
          ))}
        </div>
      </div>

      <div>
        <h4 className="font-bold text-gray-900 mb-3 flex items-center">
          <motion.div
            animate={{ rotate: [0, 5, 0] }}
            transition={{ duration: 2, repeat: Infinity, delay: 0.2 }}
          >
            <Icon name="Users" size={16} className="mr-2 text-emerald-600" />
          </motion.div>
          Instructor ({selectedInstructors.length})
        </h4>
        <div className="space-y-2 max-h-48 overflow-y-auto pr-2">
          {filterOptions.instructors.map((instructor) => (
            <motion.label
              key={instructor}
              whileHover={{ x: 5 }}
              className="flex items-center p-2 rounded-lg hover:bg-white cursor-pointer transition-all duration-300"
            >
              <input
                type="checkbox"
                checked={selectedInstructors.includes(instructor)}
                onChange={() => handleInstructorToggle(instructor)}
                className="w-4 h-4 text-emerald-600 border-gray-300 rounded focus:ring-emerald-500 focus:ring-2"
              />
              <span className="ml-3 text-sm text-gray-700">{instructor}</span>
            </motion.label>
          ))}
        </div>
      </div>

      <div>
        <h4 className="font-bold text-gray-900 mb-3 flex items-center">
          <motion.div
            animate={{ rotate: [0, 5, 0] }}
            transition={{ duration: 2, repeat: Infinity, delay: 0.4 }}
          >
            <Icon name="Folder" size={16} className="mr-2 text-green-600" />
          </motion.div>
          Category ({selectedCategories.length})
        </h4>
        <div className="space-y-2 max-h-48 overflow-y-auto pr-2">
          {filterOptions.categories.map((category) => (
            <motion.label
              key={category}
              whileHover={{ x: 5 }}
              className="flex items-center p-2 rounded-lg hover:bg-white cursor-pointer transition-all duration-300"
            >
              <input
                type="checkbox"
                checked={selectedCategories.includes(category)}
                onChange={() => handleCategoryToggle(category)}
                className="w-4 h-4 text-green-600 border-gray-300 rounded focus:ring-green-500 focus:ring-2"
              />
              <span className="ml-3 text-sm text-gray-700">{category}</span>
            </motion.label>
          ))}
        </div>
      </div>
    </div>
  );

  if (loading && webinars.length === 0) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-emerald-50/30 to-green-50/50">
        <main className="pt-14 md:pt-16 lg:pt-20">
          <div className="bg-gradient-to-r from-green-900 via-emerald-900 to-green-800 text-white relative overflow-hidden">
            {/* Animated background */}
            <div className="absolute inset-0 opacity-10">
              <div className="absolute inset-0" style={{
                backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='%23a7f3d0' fill-opacity='0.1'%3E%3Cpath d='M30 30c0-16.569-13.431-30-30-30v30h30z'/%3E%3C/g%3E%3C/svg%3E")`,
                backgroundSize: '60px 60px'
              }}></div>
            </div>
            
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 relative z-10">
              <motion.h1 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="text-4xl font-bold mb-3 bg-gradient-to-r from-emerald-300 to-green-300 bg-clip-text text-transparent"
              >
                On-Demand Compliance Library
              </motion.h1>
              <motion.p 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
                className="text-xl text-emerald-100"
              >
                Loading recorded webinars...
              </motion.p>
            </div>
          </div>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
            <div className="flex flex-col items-center justify-center space-y-4">
              <motion.div
                animate={{ 
                  rotate: 360,
                  scale: [1, 1.1, 1]
                }}
                transition={{ 
                  rotate: { duration: 2, repeat: Infinity, ease: "linear" },
                  scale: { duration: 1.5, repeat: Infinity }
                }}
                className="relative"
              >
                <div className="w-16 h-16 border-4 border-emerald-200 rounded-full"></div>
                <div className="absolute top-0 left-0 w-16 h-16 border-4 border-green-500 border-t-transparent rounded-full animate-spin"></div>
              </motion.div>
              <p className="text-gray-600">Loading recorded webinars...</p>
            </div>
          </div>
        </main>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-emerald-50/30 to-green-50/50">
        <main className="pt-14 md:pt-16 lg:pt-20">
          <div className="bg-gradient-to-r from-green-900 via-emerald-900 to-green-800 text-white">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
              <h1 className="text-4xl font-bold">On-Demand Compliance Library</h1>
            </div>
          </div>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
            <motion.div 
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              className="text-center bg-white rounded-2xl p-8 shadow-xl border border-emerald-100"
            >
              <motion.div
                animate={{ 
                  scale: [1, 1.2, 1],
                  rotate: [0, 10, 0]
                }}
                transition={{ duration: 2, repeat: Infinity }}
              >
                <Icon name="AlertCircle" size={48} className="mx-auto text-red-500 mb-4" />
              </motion.div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">Error Loading Recorded Webinars</h3>
              <p className="text-gray-600 mb-4">{error}</p>
              <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                <Button 
                  onClick={() => window.location.reload()}
                  className="bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700"
                >
                  Try Again
                </Button>
              </motion.div>
            </motion.div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-emerald-50/30 to-green-50/50">
      <main className="pt-14 md:pt-16 lg:pt-20">
        {/* Header */}
        <div className="bg-gradient-to-r from-green-900 via-emerald-900 to-green-800 text-white relative overflow-hidden">
          {/* Animated background pattern */}
          <div className="absolute inset-0 opacity-10">
            <div className="absolute inset-0" style={{
              backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='%23a7f3d0' fill-opacity='0.1'%3E%3Cpath d='M30 30c0-16.569-13.431-30-30-30v30h30z'/%3E%3C/g%3E%3C/svg%3E")`,
              backgroundSize: '60px 60px'
            }}></div>
          </div>
          
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 relative z-10">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <h1 className="text-4xl font-bold mb-3 bg-gradient-to-r from-emerald-300 to-green-300 bg-clip-text text-transparent">
                On-Demand Compliance Library
              </h1>
              <p className="text-xl text-emerald-100">
                {filteredWebinars.length} recorded webinars available • Learn anytime, anywhere
              </p>
            </motion.div>
          </div>
        </div>

        {/* ✅ PROGRESS BAR */}
        {loading && webinars.length > 0 && (
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
            <motion.div 
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="space-y-2"
            >
              <div className="flex justify-between text-sm text-gray-600">
                <span>Loading recorded webinars...</span>
                <span>{Math.round(progress)}%</span>
              </div>
              <div className="bg-gray-200 rounded-full h-2 overflow-hidden">
                <motion.div 
                  className="bg-gradient-to-r from-green-500 via-emerald-500 to-green-500 h-2 rounded-full"
                  initial={{ width: "0%" }}
                  animate={{ width: `${progress}%` }}
                  transition={{ duration: 0.5 }}
                />
              </div>
              <p className="text-xs text-gray-500">
                {Math.round(progress)}% loaded ({webinars.length}/{totalCount} webinars)
              </p>
            </motion.div>
          </div>
        )}

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Advanced Top Filters */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-gradient-to-br from-white to-emerald-50 rounded-2xl shadow-xl mb-8 overflow-hidden border-2 border-emerald-100"
          >
            <div className="p-4 md:p-6">
              <div className="flex flex-col md:flex-row gap-4">
                <div className="flex-1">
                  <div className="relative">
                    <Icon name="Search" size={18} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                    <input
                      type="text"
                      placeholder="Search recorded webinars..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="w-full pl-10 pr-4 py-3 border-2 border-emerald-200 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all duration-300"
                    />
                  </div>
                </div>

                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => isMobile ? setShowMobileDrawer(true) : setShowFilters(!showFilters)}
                  className="flex items-center justify-center gap-2 px-6 py-3 bg-gradient-to-r from-green-500 to-emerald-600 text-white rounded-xl hover:from-green-600 hover:to-emerald-700 transition-all duration-300 font-medium shadow-lg hover:shadow-green-500/30"
                >
                  <Icon name="Filter" size={18} />
                  <span>Filters</span>
                  {activeFilterCount > 0 && (
                    <motion.span 
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      className="bg-white text-green-600 px-2 py-0.5 rounded-full text-xs font-bold"
                    >
                      {activeFilterCount}
                    </motion.span>
                  )}
                  <Icon name={showFilters ? "ChevronUp" : "ChevronDown"} size={16} className="hidden md:block" />
                </motion.button>

                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  className="px-4 py-3 border-2 border-emerald-200 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all duration-300 bg-white"
                >
                  <option value="most-reviewed">Most Enrolled</option>
                  <option value="most-viewed">Most Viewed</option>
                  <option value="newest">Recently Added</option>
                  <option value="price-low">Price: Low to High</option>
                  <option value="price-high">Price: High to Low</option>
                </select>
              </div>
            </div>

            <AnimatePresence>
              {showFilters && !isMobile && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.3 }}
                  className="border-t-2 border-emerald-100 overflow-hidden"
                >
                  <div className="p-4 md:p-6 bg-gradient-to-r from-emerald-50/80 to-green-50/80">
                    <FilterSection />
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Active Filters */}
            {hasActiveFilters && (
              <motion.div 
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                className="px-4 md:px-6 py-4 bg-gradient-to-r from-emerald-50 to-green-50 border-t-2 border-emerald-100"
              >
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-sm font-medium text-gray-700">Active Filters:</span>
                  
                  {searchQuery && (
                    <motion.span 
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      className="inline-flex items-center gap-1 bg-emerald-100 text-emerald-800 px-3 py-1 rounded-full text-xs font-medium"
                    >
                      Search: "{searchQuery}"
                      <button onClick={() => setSearchQuery('')} className="hover:text-emerald-900">
                        <Icon name="X" size={12} />
                      </button>
                    </motion.span>
                  )}
                  
                  {selectedMonths.map(month => (
                    <motion.span 
                      key={month}
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      className="inline-flex items-center gap-1 bg-green-100 text-green-800 px-3 py-1 rounded-full text-xs font-medium"
                    >
                      📅 {month}
                      <button onClick={() => handleMonthToggle(month)} className="hover:text-green-900">
                        <Icon name="X" size={12} />
                      </button>
                    </motion.span>
                  ))}
                  
                  {selectedInstructors.map(instructor => (
                    <motion.span 
                      key={instructor}
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      className="inline-flex items-center gap-1 bg-emerald-100 text-emerald-800 px-3 py-1 rounded-full text-xs font-medium"
                    >
                      👤 {instructor}
                      <button onClick={() => handleInstructorToggle(instructor)} className="hover:text-emerald-900">
                        <Icon name="X" size={12} />
                      </button>
                    </motion.span>
                  ))}
                  
                  {selectedCategories.map(category => (
                    <motion.span 
                      key={category}
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      className="inline-flex items-center gap-1 bg-green-100 text-green-800 px-3 py-1 rounded-full text-xs font-medium"
                    >
                      📂 {category}
                      <button onClick={() => handleCategoryToggle(category)} className="hover:text-green-900">
                        <Icon name="X" size={12} />
                      </button>
                    </motion.span>
                  ))}
                  
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={clearFilters}
                    className="ml-auto text-sm text-emerald-600 hover:text-emerald-800 font-medium underline"
                  >
                    Clear All
                  </motion.button>
                </div>
              </motion.div>
            )}
          </motion.div>

          {/* Results Header */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="flex justify-between items-center mb-6"
          >
            <h2 className="text-2xl font-bold text-gray-900">
              {filteredWebinars.length} Result{filteredWebinars.length !== 1 ? 's' : ''}
            </h2>
          </motion.div>

          {/* Webinars Grid */}
          {paginatedWebinars.length === 0 ? (
            <motion.div 
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              className="text-center py-12 bg-gradient-to-br from-white to-emerald-50 rounded-2xl border-2 border-emerald-100 shadow-xl"
            >
              <motion.div
                animate={{ rotate: [0, 10, 0] }}
                transition={{ duration: 2, repeat: Infinity }}
              >
                <Icon name="Search" size={48} className="mx-auto text-gray-400 mb-4" />
              </motion.div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">No recorded webinars found</h3>
              <p className="text-gray-600 mb-4">Try adjusting your filters</p>
              <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                <Button 
                  onClick={clearFilters}
                  className="bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700"
                >
                  Clear Filters
                </Button>
              </motion.div>
            </motion.div>
          ) : (
            <>
              <motion.div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
                {paginatedWebinars.map((webinar, index) => {
                  const slug = generateSlug(webinar.title);
                  const webinarUrl = `/recorded-webinar/${webinar.webinarId}/${slug}`;

                  return (
                    <motion.div
                      key={webinar.id}
                      initial={{ opacity: 0, y: 30 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.05, type: "spring", stiffness: 100 }}
                      whileHover={{ 
                        y: -8,
                        boxShadow: "0 20px 40px -10px rgba(16, 185, 129, 0.2)"
                      }}
                      className="bg-gradient-to-br from-white to-emerald-50 rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 overflow-hidden border-2 border-emerald-100 group relative"
                    >
                      <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-green-500 via-emerald-500 to-green-500"></div>
                      
                      <Link to={webinarUrl} className="block cursor-pointer">
                        <div className="relative overflow-hidden h-44">
                          <motion.img
                            src={webinar.image}
                            alt={webinar.title}
                            className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                            loading="lazy"
                            whileHover={{ scale: 1.1 }}
                          />
                          <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />

                          <div className="absolute top-3 left-3">
                            <motion.span 
                              whileHover={{ scale: 1.1 }}
                              className="bg-gradient-to-r from-green-500 to-emerald-600 text-white text-xs font-bold px-3 py-1.5 rounded-full flex items-center gap-1.5 shadow-lg"
                            >
                              <Icon name="Play" size={12} />
                              ON-DEMAND
                            </motion.span>
                          </div>

                          <motion.div 
                            whileHover={{ scale: 1.1 }}
                            className="absolute top-3 right-3 bg-white rounded-xl px-3 py-1.5 shadow-lg border-2 border-emerald-200"
                          >
                            <div className="text-sm font-black text-transparent bg-gradient-to-br from-green-600 to-emerald-600 bg-clip-text">
                              {webinar.year}
                            </div>
                          </motion.div>
                        </div>

                        <div className="p-5">
                          <h3 className="font-bold text-gray-900 mb-2 line-clamp-2 text-sm leading-tight hover:text-emerald-700 transition-colors h-10">
                            {webinar.title}
                          </h3>

                          <div className="flex items-center justify-between mb-3">
                            <div className="flex items-center gap-1.5 flex-1 min-w-0">
                              <motion.img
                                src={webinar.instructor.avatar}
                                alt={webinar.instructor.name}
                                className="w-6 h-6 rounded-full object-cover border-2 border-emerald-200"
                                loading="lazy"
                                whileHover={{ scale: 1.2 }}
                              />
                              <span className="text-xs text-gray-600 truncate">{webinar.instructor.name}</span>
                            </div>
                            <div className="flex items-center gap-1 ml-2">
                              <Icon name="Star" size={12} className="text-yellow-400 fill-current" />
                              <span className="text-xs font-bold text-gray-900">{webinar.rating.toFixed(1)}</span>
                            </div>
                          </div>

                          <div className="flex items-center justify-between text-xs text-gray-600 mb-3 bg-gradient-to-r from-emerald-50 to-green-50 rounded-xl p-2 border border-emerald-100">
                            <div className="flex items-center gap-1">
                              <Icon name="Clock" size={12} className="text-emerald-600" />
                              <span className="font-medium">
                                {webinar.duration === 'custom'
                                  ? `${webinar.rawDurationMinutes} min`
                                  : `${webinar.duration} min`}
                              </span>
                            </div>
                            <div className="flex items-center gap-1">
                              <Icon name="Users" size={12} className="text-green-600" />
                              <span className="font-medium">{webinar.enrollments}</span>
                            </div>
                          </div>

                          <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl p-2.5 mb-3 border border-emerald-200">
                            <div className="text-xs text-emerald-700 font-semibold flex items-center gap-1.5">
                              <Icon name="PlayCircle" size={14} className="text-green-600" />
                              Watch Anytime • Learn Anytime
                            </div>
                          </div>

                          <div className="flex items-center justify-between gap-2">
                            <div className="flex items-baseline gap-1">
                              <span className="text-xl font-black text-transparent bg-gradient-to-r from-green-600 to-emerald-600 bg-clip-text">
                                ${webinar.price.toFixed(2)}
                              </span>
                            </div>
                            <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                              <Link
                                to={webinarUrl}
                                onClick={(e) => e.stopPropagation()}
                                className="bg-gradient-to-r from-emerald-500 to-green-600 text-white font-bold py-2 px-4 rounded-xl text-xs hover:shadow-lg hover:shadow-emerald-500/50 transition-all flex items-center gap-1"
                              >
                                Watch Now
                                <Icon name="Play" size={12} />
                              </Link>
                            </motion.div>
                          </div>
                        </div>
                      </Link>
                    </motion.div>
                  );
                })}
              </motion.div>

              {/* Pagination */}
              {totalPages > 1 && (
                <motion.div 
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3 }}
                  className="flex justify-center items-center space-x-2 mt-12 pt-8"
                >
                  <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                    <Button
                      variant="outline"
                      disabled={currentPage === 1}
                      onClick={() => setCurrentPage(currentPage - 1)}
                      className="text-gray-700 border-emerald-300 hover:bg-emerald-50 hover:border-emerald-400 transition-all duration-300"
                    >
                      Previous
                    </Button>
                  </motion.div>
                  
                  <div className="flex space-x-1">
                    {Array.from({ length: Math.min(totalPages, 7) }, (_, i) => {
                      let page = i + 1;
                      if (totalPages > 7) {
                        if (currentPage <= 4) page = i + 1;
                        else if (currentPage >= totalPages - 3) page = totalPages - 6 + i;
                        else page = currentPage - 3 + i;
                      }
                      return (
                        <motion.button
                          key={page}
                          whileHover={{ scale: 1.1 }}
                          whileTap={{ scale: 0.9 }}
                          onClick={() => setCurrentPage(page)}
                          className={`w-10 h-10 rounded-xl text-sm font-medium transition-all duration-300 ${
                            currentPage === page
                              ? 'bg-gradient-to-r from-emerald-500 to-green-600 text-white shadow-lg shadow-emerald-500/30'
                              : 'bg-white border-2 border-emerald-200 text-gray-700 hover:bg-emerald-50 hover:border-emerald-300'
                          }`}
                        >
                          {page}
                        </motion.button>
                      );
                    })}
                  </div>

                  <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                    <Button
                      variant="outline"
                      disabled={currentPage === totalPages}
                      onClick={() => setCurrentPage(currentPage + 1)}
                      className="text-gray-700 border-emerald-300 hover:bg-emerald-50 hover:border-emerald-400 transition-all duration-300"
                    >
                      Next
                    </Button>
                  </motion.div>
                </motion.div>
              )}
            </>
          )}
        </div>
      </main>
    </div>
  );
};

export default RecordedWebinarsPage;